import React, { useState, useEffect, useRef } from 'react'
import { Card, Form, Input, Button, Typography, Space, Divider, App, Tabs, Menu } from 'antd'
import { UserOutlined, LockOutlined, VideoCameraOutlined, MobileOutlined, HomeOutlined, PictureOutlined, HistoryOutlined, CreditCardOutlined } from '@ant-design/icons'
import { Link, useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { useAuthStore } from '@/store/authStore'
import { authService } from '@/services/authService'
import { useSiteConfigStore } from '@/store/siteConfigStore'

const { Title, Paragraph, Text } = Typography

const LoginPage: React.FC = () => {
  const { message } = App.useApp()
  const [form] = Form.useForm()
  const [phoneForm] = Form.useForm()
  const [loading, setLoading] = useState(false)
  const [smsLoading, setSmsLoading] = useState(false)
  const [countdown, setCountdown] = useState(0)
  const [autoAgreed, setAutoAgreed] = useState(false)
  const countdownTimerRef = useRef<number | null>(null)
  const { login, phoneLogin } = useAuthStore()
  const navigate = useNavigate()
  const { config: siteConfig } = useSiteConfigStore()

  // 清理计时器
  useEffect(() => {
    return () => {
      if (countdownTimerRef.current) {
        clearInterval(countdownTimerRef.current)
      }
    }
  }, [])

  const resolveRedirectPath = () => {
    const { user } = useAuthStore.getState()
    if (user?.role === 'ADMIN') return '/admin'
    if (user?.role === 'AGENT_ADMIN') return '/agent-portal'
    return '/'
  }

  const handleLogin = async (values: any) => {
    setLoading(true)
    try {
      await login(values)
      message.success('登录成功')
      navigate(resolveRedirectPath())
    } catch (error: any) {
      message.error(error.response?.data?.message || '登录失败')
    } finally {
      setLoading(false)
    }
  }

  // 发送短信验证码
  const handleSendSms = async () => {
    const phone = phoneForm.getFieldValue('phone')
    if (!phone) {
      message.error('请先输入手机号')
      return
    }

    if (!/^1[3-9]\d{9}$/.test(phone)) {
      message.error('请输入有效的手机号')
      return
    }

    setSmsLoading(true)
    try {
      const response = await authService.sendSmsCode(phone, 'LOGIN')
      message.success('验证码已发送')
      
      // 开发环境显示验证码
      if (response.data?.code) {
        console.log('验证码（开发环境）:', response.data.code)
      }
      
      // 清理之前的计时器
      if (countdownTimerRef.current) {
        clearInterval(countdownTimerRef.current)
      }
      
      setCountdown(60)
      countdownTimerRef.current = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            if (countdownTimerRef.current) {
              clearInterval(countdownTimerRef.current)
            }
            return 0
          }
          return prev - 1
        })
      }, 1000)
    } catch (error: any) {
      message.error(error.response?.data?.message || '发送验证码失败')
    } finally {
      setSmsLoading(false)
    }
  }

  // 手机登录
  const handlePhoneLogin = async (values: any) => {
    setLoading(true)
    try {
      // 验证手机号和验证码格式
      if (!values.phone || !/^1[3-9]\d{9}$/.test(values.phone)) {
        message.error('请输入有效的手机号')
        setLoading(false)
        return
      }
      if (!values.code || !/^\d{6}$/.test(values.code)) {
        message.error('验证码必须是6位数字')
        setLoading(false)
        return
      }
      
      await phoneLogin(values.phone, values.code)
      message.success('登录成功')
      navigate(resolveRedirectPath())
    } catch (error: any) {
      // 处理验证错误
      if (error.response?.data?.errors) {
        const errorMsg = error.response.data.errors
          .map((err: any) => err.message)
          .join('; ')
        message.error(errorMsg || error.response?.data?.message || '登录失败')
      } else {
      message.error(error.response?.data?.message || '登录失败')
      }
    } finally {
      setLoading(false)
    }
  }

  // 验证码输入自动检查和登录
  const handleCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const code = e.target.value
    const phone = phoneForm.getFieldValue('phone')
    
    // 当验证码输入完整且手机号有效时，自动登录
    if (code.length === 6 && phone && /^1[3-9]\d{9}$/.test(phone)) {
      // 自动同意协议
      setAutoAgreed(true)
      
      // 延迟500ms后自动登录，将message调用移到异步操作中
      setTimeout(() => {
        phoneForm.setFieldsValue({ code })
        // 在异步回调中调用message，避免在render期间调用
        message.success('已自动同意用户协议，正在登录...')
        handlePhoneLogin({ phone, code })
      }, 500)
    }
  }

  // 导航菜单项
  const menuItems = [
    {
      key: '/',
      icon: <HomeOutlined />,
      label: <Link to="/" className="navbar-center" style={{ fontSize: '14px', color: '#555', textDecoration: 'none', padding: '6px 10px', borderRadius: '4px', transition: 'background 0.2s' }}>首页</Link>,
    },
    {
      key: '/video',
      icon: <VideoCameraOutlined />,
      label: <Link to="/video" className="navbar-center" style={{ fontSize: '14px', color: '#555', textDecoration: 'none', padding: '6px 10px', borderRadius: '4px', transition: 'background 0.2s' }}>视频生成</Link>,
    },
    {
      key: '/image',
      icon: <PictureOutlined />,
      label: <Link to="/image" className="navbar-center" style={{ fontSize: '14px', color: '#555', textDecoration: 'none', padding: '6px 10px', borderRadius: '4px', transition: 'background 0.2s' }}>图像生成</Link>,
    },
    {
      key: '/history',
      icon: <HistoryOutlined />,
      label: <Link to="/history" className="navbar-center" style={{ fontSize: '14px', color: '#555', textDecoration: 'none', padding: '6px 10px', borderRadius: '4px', transition: 'background 0.2s' }}>历史记录</Link>,
    },
    {
      key: '/payment',
      icon: <CreditCardOutlined />,
      label: <Link to="/payment" className="navbar-center" style={{ fontSize: '14px', color: '#555', textDecoration: 'none', padding: '6px 10px', borderRadius: '4px', transition: 'background 0.2s' }}>积分充值</Link>,
    },
  ]

  return (
    <div className="min-h-screen flex flex-col login-page">
      {/* 顶部导航栏 */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-50" style={{ height: '56px', fontFamily: 'Segoe UI, sans-serif' }}>
        <div className="container-max flex items-center justify-between max-w-7xl mx-auto h-full">
          {/* Logo */}
          <motion.div 
            className="flex items-center"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
            style={{ gap: '12px' }}
          >
            <Link to="/" className="navbar-left flex items-center transition-colors duration-200" style={{ gap: '12px', fontWeight: '600', fontSize: '15px', color: '#333', textDecoration: 'none' }}>
              {siteConfig?.siteLogo ? (
                <img 
                  src={siteConfig.siteLogo} 
                  alt={siteConfig.siteName || 'Logo'} 
                  className="h-8 object-contain"
                />
              ) : (
                <>
                  <div className="bg-gradient-to-r from-blue-500 to-purple-600 flex items-center justify-center" style={{
                    width: 'var(--spacing-xl)',
                    height: 'var(--spacing-xl)',
                    borderRadius: 'var(--radius-lg)'
                  }}>
                    <VideoCameraOutlined className="text-white text-lg" />
                  </div>
                  <span className="font-semibold block">
                    {siteConfig?.siteName || 'Sora2 AI'}
                  </span>
                </>
              )}
            </Link>
          </motion.div>

          {/* 导航菜单 */}
          <div className="hidden md:flex items-center" style={{ gap: '20px' }}>
            <Menu
              mode="horizontal"
              selectedKeys={[]}
              items={menuItems}
              className="border-none bg-transparent"
              style={{
                backgroundColor: 'transparent',
                border: 'none'
              }}
              triggerSubMenuAction="click"
            />
          </div>

          {/* 右侧按钮 */}
          <div className="flex items-center" style={{ gap: '12px' }}>
            <Button 
              type="text" 
              onClick={() => navigate('/login')}
              className="text-gray-600 hover:text-blue-500 hover:bg-gray-100 rounded transition-all duration-200"
              style={{ padding: '6px 10px', fontSize: '14px' }}
            >
              登录
            </Button>
            <Button 
              type="primary" 
              onClick={() => navigate('/register')}
              className="bg-blue-600 border-none rounded transition-all duration-200"
              style={{ padding: '6px 12px', fontSize: '14px' }}
            >
              注册
            </Button>
          </div>
        </div>
      </div>

      {/* 登录表单区域 */}
      <div className="flex-1 bg-gradient-to-br from-blue-50/60 via-purple-50/40 to-pink-50/60 flex items-center justify-center pt-4 pb-0 md:py-12 px-2 md:px-4 relative overflow-hidden">
      {/* 背景装饰 */}
      <div className="absolute inset-0 bg-gradient-to-br from-blue-100/20 via-purple-100/10 to-pink-100/20"></div>
      <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-blue-200/20 rounded-full blur-3xl"></div>
      <div className="absolute bottom-1/4 right-1/4 w-64 h-64 bg-purple-200/20 rounded-full blur-3xl"></div>
      
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md relative z-10 md:my-0"
        style={{ marginTop: 'auto', marginBottom: '0' }}
      >
        {/* Logo和标题 */}
        <div
          className="flex items-center justify-center text-left gap-3"
          style={{ marginBottom: 'var(--spacing-xl)' }}
        >
          <motion.div
            initial={{ scale: 0.8 }}
            animate={{ scale: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="flex items-center justify-center bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg"
            style={{ width: 'var(--spacing-xl)', height: 'var(--spacing-xl)' }}
          >
            <VideoCameraOutlined className="text-white text-lg" />
          </motion.div>
          <div className="flex flex-col justify-center">
            <Title
              level={3}
              className="unified-title-h3"
              style={{
                marginBottom: 'var(--spacing-xs)',
                color: '#333'
              }}
            >
              Sora2 AI
            </Title>
            <Paragraph
              className="text-gray-500"
              style={{
                fontSize: 'var(--font-small)',
                marginBottom: 0
              }}
            >
              欢迎回来，开始您的AI创作之旅
            </Paragraph>
          </div>
        </div>

        {/* 登录表单 */}
        <Card className="unified-card">
          <Tabs
            defaultActiveKey="phone"
            items={[
              {
                key: 'phone',
                label: '手机登录',
                children: (
                  <Form
                    form={phoneForm}
                    layout="vertical"
                    onFinish={handlePhoneLogin}
                    size="large"
                  >
                    <Form.Item
                      name="phone"
                      label={<span className="text-gray-700 font-semibold">手机号</span>}
                      rules={[
                        { required: true, message: '请输入手机号' },
                        { pattern: /^1[3-9]\d{9}$/, message: '请输入有效的手机号' }
                      ]}
                    >
                      <Input
                        prefix={<MobileOutlined className="text-blue-500" />}
                        placeholder="请输入手机号"
                        className="unified-input"
                      />
                    </Form.Item>

                    <Form.Item
                      label={<span className="text-gray-700 font-semibold">验证码</span>}
                    >
                      <Space.Compact style={{ width: '100%', gap: 'var(--spacing-sm)' }}>
                        <Form.Item
                          name="code"
                          noStyle
                          rules={[
                            { required: true, message: '请输入验证码' },
                            { pattern: /^\d{6}$/, message: '请输入6位数字验证码' }
                          ]}
                        >
                          <Input
                            placeholder="请输入验证码"
                            style={{ flex: 1 }}
                            className="unified-input"
                            maxLength={6}
                            onChange={handleCodeChange}
                          />
                        </Form.Item>
                        <Button
                          onClick={handleSendSms}
                          disabled={countdown > 0 || smsLoading}
                          loading={smsLoading}
                          className="unified-button"
                          style={{ flexShrink: 0 }}
                        >
                          {countdown > 0 ? `${countdown}s` : '获取验证码'}
                        </Button>
                      </Space.Compact>
                    </Form.Item>

                    <Form.Item style={{ marginTop: 0 }}>
                      <Button
                        type="primary"
                        htmlType="submit"
                        loading={loading}
                        block
                        className="unified-button"
                        style={{ height: '48px', fontSize: '16px' }}
                      >
                        登录
                      </Button>
                    </Form.Item>

                    <Divider>或</Divider>

                    <div className="text-center">
                      <Text className="text-gray-600">
                        还没有账户？{' '}
                        <Link to="/register" className="text-blue-500 hover:text-blue-600 font-medium">
                          立即注册
                        </Link>
                      </Text>
                    </div>
                  </Form>
                )
              },
              {
                key: 'email',
                label: '邮箱登录',
                children: (
          <Form
            form={form}
            layout="vertical"
            onFinish={handleLogin}
            size="large"
          >
            <Form.Item
              name="email"
              label={<span className="text-gray-700 font-semibold">邮箱</span>}
              rules={[
                { required: true, message: '请输入邮箱' },
                { type: 'email', message: '请输入有效的邮箱地址' }
              ]}
            >
              <Input
                prefix={<UserOutlined className="text-blue-500" />}
                placeholder="请输入邮箱"
                className="unified-input"
              />
            </Form.Item>

            <Form.Item
              name="password"
              label={<span className="text-gray-700 font-semibold">密码</span>}
              rules={[{ required: true, message: '请输入密码' }]}
            >
              <Input.Password
                prefix={<LockOutlined className="text-blue-500" />}
                placeholder="请输入密码"
                className="unified-input"
              />
            </Form.Item>

            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                block
                className="unified-button"
                style={{ height: '48px', fontSize: '16px' }}
              >
                登录
              </Button>
            </Form.Item>

                    <div className="text-center" style={{ marginBottom: 'var(--spacing-lg)' }}>
              <Link to="/forgot-password" className="text-blue-500 hover:text-blue-600">
                忘记密码？
              </Link>
            </div>

            <Divider>或</Divider>

            <div className="text-center">
              <Text className="text-gray-600">
                还没有账户？{' '}
                <Link to="/register" className="text-blue-500 hover:text-blue-600 font-medium">
                  立即注册
                </Link>
              </Text>
            </div>
          </Form>
                )
              }
            ]}
          />
        </Card>

        {/* 底部链接 */}
        <div className="text-center" style={{ marginTop: 'var(--spacing-xl)', marginBottom: 'var(--spacing-md)' }}>
          <Space direction="vertical" size="small">
            <Text className={`text-sm ${autoAgreed ? 'text-green-600' : 'text-gray-500'}`}>
              {autoAgreed ? '✓ 已自动同意用户协议' : '输入完整验证码将自动同意协议并登录'}
            </Text>
            <Space size="middle">
              <Link to="/terms" className="text-gray-500 hover:text-gray-700 text-sm">
                服务条款
              </Link>
              <Text className="text-gray-400">|</Text>
              <Link to="/privacy" className="text-gray-500 hover:text-gray-700 text-sm">
                隐私政策
              </Link>
            </Space>
          </Space>
        </div>
      </motion.div>
      </div>
    </div>
  )
}

export default LoginPage
